import { useEffect, useMemo, lazy, Suspense } from 'react';
import { CSSTransition } from 'react-transition-group';
import { Box, useMediaQuery, useTheme } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { shopImageSrcSet, shopImageUrl } from 'utilities/images';
import clsx from 'clsx';
import ThreeSixtyView, {
  AnimationTypes,
} from 'components/common/three_sixty_view';
import GalleryViewLoader from 'components/common/gallery_view_loader';
import threeSixtyIcon from 'images/icons/360.svg';
import threeDimensionalIcon from 'images/icons/3D.svg';
import { shouldView3dArModel } from 'helpers/search_parameter_helpers';
import {
  shouldRenderThreeDimensionalView,
  shouldRenderThreeSixtyView,
} from 'utilities/gpu_capabilities';

import { useProductContext } from 'typescript/pages/product/contexts/product_context';

// Lazy load ThreeDimensionalView to prevent @google/model-viewer from loading
// until we actually need to render the 3D view. This prevents WebGL initialization
// errors when hardware acceleration is disabled.
const ThreeDimensionalView = lazy(
  () => import('components/common/three_dimensional_view'),
);

const useThumbStyles = makeStyles((theme) => ({
  thumbButton: {
    margin: theme.spacing(0, 0.5, 0.5),
    cursor: 'pointer',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
    backgroundSize: ({ iconSrc }) => (iconSrc ? '30px, auto, cover' : 'cover'),
    backgroundImage: ({ src, iconSrc }) =>
      iconSrc
        ? `url(${iconSrc}), linear-gradient(rgba(255,255,255,.8), rgba(255,255,255,.8)), url(${src})`
        : `url(${src})`,
    boxShadow: ({ active }) =>
      active
        ? `inset 0 0 0 2px ${theme.color.blue}`
        : `inset 0 0 0 1px ${theme.color.border}`,
    border: 'none',
    borderRadius: theme.border.radius.base,
    width: '52px',
    height: '52px',
    '&:hover, &:focus': {
      outline: 'none',
    },
  },
}));

const useStyles = makeStyles({
  galleryRoot: {
    position: 'relative',
    paddingTop: ({ aspectRatio }) => `calc(1 / ${aspectRatio} * 100%)`,
    width: '100%',
  },
  galleryImageWrapper: {
    position: 'absolute',
    left: 0,
    top: 0,
    opacity: 0,
    height: '100%',
    width: '100%',
    transition: ({ transitionDuration }) => `opacity ${transitionDuration}ms`,
  },
  galleryPicture: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    width: '100%',
  },
  galleryMultiMediaWrapper: {
    opacity: 0,
    transition: ({ transitionDuration }) => `opacity ${transitionDuration}ms`,
  },
  galleryImage: {
    display: 'block',
    width: 'auto',
    height: 'auto',
    maxWidth: '100%',
    maxHeight: '100%',
  },
});

const useFadeStyles = makeStyles({
  appear: { opacity: 1 },
  enter: { opacity: 0 },
  enterActive: { opacity: 1 },
  enterDone: { opacity: 1 },
  exit: { opacity: 1 },
  exitActive: { opacity: 0 },
});

const Thumb = ({ active, setActive, src, idx, iconSrc }) => {
  const classes = useThumbStyles({ active, src, iconSrc });

  return (
    <button
      onMouseEnter={() => setActive(idx)}
      className={`${classes.thumbButton} ${active ? 'monochrome-override-thumb' : ''} ${iconSrc ? 'monochrome-override-thumb-icon' : ''}`}
    />
  );
};

const ImageGallery = (props) => {
  const {
    images,
    threeSixty: {
      title,
      initial_rotation_percentage,
      images: threeSixtyImages = [],
    } = {},
    threeDimensionalModel: { product_models = [] } = {},
    activeImageIndex = 0,
    setActiveImageIndex,
    setQrModalOpen,
    setQrCodeUrl,
    aspectRatio = 1,
    transitionDuration = 100,
    classes: { galleryRoot } = {},
  } = props;

  const { cart, currentProduct: product } = useProductContext();

  const MAX_THUMBS = 5;

  const theme = useTheme();
  const classes = useStyles({ aspectRatio, transitionDuration });
  const fadeClasses = useFadeStyles();
  const mobile = useMediaQuery(theme.breakpoints.down('md'));

  const hasThreeSixtyData = title && threeSixtyImages.length > 0;
  const hasThreeDimensionalData = product_models.length > 0;
  const hasThreeSixty = hasThreeSixtyData && shouldRenderThreeSixtyView();
  const hasThreeDimensional =
    hasThreeDimensionalData && shouldRenderThreeDimensionalView();

  const imgSrc = useMemo(
    () =>
      images.map((url) => ({
        jpgSrcSet: shopImageSrcSet({
          baseUrl: url,
          type: 'gallery',
          fm: 'jpg',
        }),
        webpSrcSet: shopImageSrcSet({
          baseUrl: url,
          type: 'gallery',
          fm: 'webp',
        }),
        thumbSrc: shopImageUrl({
          baseUrl: url,
          type: 'galleryThumb',
          size: '2x',
          fm: 'jpg',
        }),
      })),
    [images],
  );

  const threeSixtyThumbIndex = imgSrc.length + 1;
  const threeDimensionalThumbIndex = imgSrc.length + (hasThreeSixty ? 2 : 1);

  // TODO (@omose): Work with design to come up with better way to handle overflowing gallery images
  const buildThumbnailsRows = () => {
    if (imgSrc.length > 1 || hasThreeSixty) {
      const rows = [];
      const thumbnails = imgSrc.map(({ thumbSrc }, i) => (
        <Thumb
          key={i}
          idx={i}
          src={thumbSrc}
          active={activeImageIndex === i}
          setActive={setActiveImageIndex}
        />
      ));

      if (hasThreeSixty) {
        thumbnails.push(
          <Thumb
            key="three-sixty"
            idx={threeSixtyThumbIndex}
            src={imgSrc[0].thumbSrc}
            iconSrc={threeSixtyIcon}
            active={activeImageIndex === threeSixtyThumbIndex}
            setActive={setActiveImageIndex}
          />,
        );
      }

      if (hasThreeDimensional) {
        thumbnails.push(
          <Thumb
            key="three-dimensional"
            idx={threeDimensionalThumbIndex}
            src={imgSrc[0].thumbSrc}
            iconSrc={threeDimensionalIcon}
            active={activeImageIndex === threeDimensionalThumbIndex}
            setActive={setActiveImageIndex}
          />,
        );
      }

      // Splits thumbnails into rows of MAX_THUMBS icons
      if (mobile && thumbnails.length > MAX_THUMBS) {
        let currentIndex = 0;
        while (currentIndex < thumbnails.length) {
          rows.push(thumbnails.slice(currentIndex, currentIndex + MAX_THUMBS));
          currentIndex += MAX_THUMBS;
        }
      } else {
        rows.push(thumbnails);
      }

      return (
        <>
          {rows.map((row, index) => (
            <Box
              key={index}
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                mt: index === 0 ? 2 : 0,
                mb: index === rows.length - 1 ? 2 : 0,
                justifyContent: 'center',
                flexGrow: 1,
                maxWidth: '85%',
              }}
            >
              {row}
            </Box>
          ))}
        </>
      );
    }

    return null;
  };

  useEffect(() => {
    if (shouldView3dArModel() && hasThreeDimensional) {
      setActiveImageIndex(threeDimensionalThumbIndex);
    }
  }, [images]);

  return (
    <>
      <div className={clsx(classes.galleryRoot, galleryRoot)}>
        {imgSrc.map(({ jpgSrcSet, webpSrcSet }, i) => (
          <CSSTransition
            key={i}
            in={activeImageIndex === i}
            timeout={1000}
            classNames={fadeClasses}
            appear
          >
            <div className={classes.galleryImageWrapper}>
              <picture className={classes.galleryPicture}>
                <source type="image/webp" srcSet={webpSrcSet} />
                {/* The first gallery image is the LCP element. React 18
                    only forwards the lowercase attribute name. */}
                <img
                  className={classes.galleryImage}
                  src={jpgSrcSet}
                  fetchpriority={i === 0 ? 'high' : undefined}
                />
              </picture>
            </div>
          </CSSTransition>
        ))}

        {hasThreeSixty && (
          <CSSTransition
            key="three-sixty"
            in={activeImageIndex === threeSixtyThumbIndex}
            timeout={1000}
            classNames={fadeClasses}
            appear
          >
            <div className={classes.galleryMultiMediaWrapper}>
              <ThreeSixtyView
                title={title}
                images={threeSixtyImages}
                cart={cart}
                product={product}
                initialRotationPercentage={initial_rotation_percentage || 0}
                lazyLoad={true}
                inView={activeImageIndex === threeSixtyThumbIndex}
                introAnimation={AnimationTypes.FullRevolution}
              />
            </div>
          </CSSTransition>
        )}

        {/* We can not have full control of the 3D models loading as we would like (https://modelviewer.dev/docs/index.html#entrydocs-loading-attributes-loading).
        To keep this from view downloading 3D models sooner, we only add the view when we click on the view 3D button.
        ThreeDimensionalView is lazy-loaded to prevent @google/model-viewer from loading until needed. */}
        {hasThreeDimensional &&
          activeImageIndex === threeDimensionalThumbIndex && (
            <Suspense fallback={<GalleryViewLoader />}>
              <CSSTransition
                key="three-dimensional"
                in={activeImageIndex === threeDimensionalThumbIndex}
                timeout={1000}
                classNames={fadeClasses}
                appear
              >
                <div className={classes.galleryMultiMediaWrapper}>
                  <ThreeDimensionalView
                    models={product_models}
                    product={product}
                    cart={cart}
                    setQrModalOpen={setQrModalOpen}
                    setQrCodeUrl={setQrCodeUrl}
                  />
                </div>
              </CSSTransition>
            </Suspense>
          )}
      </div>
      {buildThumbnailsRows()}
    </>
  );
};

export default ImageGallery;
